/**
 * Compact inline notice for a SECONDARY read that failed on a weak connection
 * (ISS-445 / #4602), where the surrounding surface still has real data to show.
 *
 * The third member of the degraded-read family, and the one the other two cannot
 * cover:
 *   - DegradedReadState  — the whole surface failed and there is nothing to show.
 *   - StaleDataBanner    — the data IS on screen but could not be refreshed.
 *   - this               — the surface is fine; an ENRICHMENT read failed, so some
 *                          affordance is missing rather than absent.
 *
 * Why it exists: service-history rows come from one query and their "View Report" /
 * "Job Folder" links from another. When only the second fails, the rows still render
 * and the links simply are not there — which reads as "this visit produced no report"
 * rather than "we could not check". A full-surface retry state would be wrong (it
 * would hide history that loaded correctly); silence is worse. Say what is unknown,
 * in place, and let it be retried.
 */
import { CloudOff } from 'lucide-react';
import { Button } from '../forms/Button';

interface DegradedLinksNoticeProps {
  /**
   * What could not be checked, as a noun phrase completing
   * "Weak connection — couldn't check for ___". Keep it about the missing
   * LOOKUP, never about the data ("no reports" would be the lie this fixes).
   */
  what: string;
  /** Re-run the failed read. Omit when the caller has no refetch to offer. */
  onRetry?: () => void;
  /** True while the retry is in flight. */
  isRetrying?: boolean;
}

export function DegradedLinksNotice({
  what,
  onRetry,
  isRetrying = false,
}: DegradedLinksNoticeProps) {
  return (
    <div
      role="status"
      className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800"
    >
      <CloudOff className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span className="flex-1">Weak connection &mdash; couldn't check for {what}.</span>
      {onRetry && (
        <Button
          variant="warning"
          size="sm"
          onClick={onRetry}
          loading={isRetrying}
          loadingText="Checking..."
        >
          Retry
        </Button>
      )}
    </div>
  );
}
